import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { NGXLogger } from 'ngx-logger';
import { PolicyContentData } from '../../common/interfaces';
import { GET_POLICY_PAGE } from '../../common/graphql/documents.graphql';
import { GetPolicyPageQuery, GetPolicyPageQueryVariables } from '../../common/generated-types';
import { DataService } from './data/data.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { StateService } from './state/state.service';

const POLICY_STORE_IDENTIFIER_KEY = makeStateKey<string>('POLICY_STORE_IDENTIFIER');
@Injectable({ providedIn: 'root' })
export class PolicyResolver implements Resolve<PolicyContentData | null> {
  policySlug: string;
  storeIdentifier: string;
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('STORE_IDENTIFIER') public storeIdentifierFromSSR: string | null,
    private dataService: DataService,
    private stateService: StateService,
    private transferState: TransferState,
    private router: Router,
    private logger: NGXLogger
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    this.policySlug = route.paramMap.get('policy')?.toLowerCase() || '';
    if (isPlatformServer(this.platformId)) {
      this.storeIdentifier = this.storeIdentifierFromSSR || '';
      this.transferState.set(POLICY_STORE_IDENTIFIER_KEY, this.storeIdentifier);
    }
    return this.stateService.select(state => state.storeIdentifier).pipe(
      switchMap(storeIdentifier => {
        if (isPlatformBrowser(this.platformId)) {
          if(!storeIdentifier) {
            this.storeIdentifier = this.transferState.get(POLICY_STORE_IDENTIFIER_KEY, '');
            this.transferState.remove(POLICY_STORE_IDENTIFIER_KEY);
          } else {
            this.storeIdentifier = storeIdentifier;
          }
        }
        this.logger.debug(`PolicyResolver for ${this.policySlug} ${storeIdentifier}`);
        return this.dataService.query<GetPolicyPageQuery, GetPolicyPageQueryVariables>(GET_POLICY_PAGE, {
          identifier: this.storeIdentifier,
          slug: this.policySlug
        }).pipe(
          map(result => {
            this.logger.debug(`PolicyResolver: result ${result}`);
            if (result?.policyPage !== null) {
              return result?.policyPage as PolicyContentData;
            } else {
              this.logger.error(`PolicyResolver: Policy Page not found for slug '${this.policySlug}'`);
              this.router.navigateByUrl('/not-found', { skipLocationChange: true });
              return null;
            }
          }),
          catchError((err) => {
            // In case of errors, also go to not-found
            this.logger.error(`PolicyResolver: Error ${err} fetching product page for slug '${this.policySlug}'`);
            this.router.navigateByUrl('/not-found', { skipLocationChange: true });
            return of(null);
          })
        );
      })
    );
  }
}
